import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// ProgressCircle – SVG circular progress indicator with optional label
// ---------------------------------------------------------------------------

interface ProgressCircleSignature {
  Args: {
    /** Progress value 0-100 */
    progress: number;
    /** Size of the circle in pixels */
    size?: 'sm' | 'md' | 'lg' | number;
    /** Stroke width */
    strokeWidth?: number;
    /** Progress bar color (CSS color value, e.g. hex/rgb/named color) */
    progressColor?: string;
    /** Background track color (CSS color value, e.g. hex/rgb/named color) */
    trackColor?: string;
    /** Show percentage label in center */
    showLabel?: boolean;
    /** Custom label (overrides percentage) */
    label?: string;
    /** Secondary text below label */
    sublabel?: string;
  };
  Element: HTMLDivElement;
}

export default class ProgressCircle extends Component<ProgressCircleSignature> {
  get sizeValue() {
    const sizeMap = { sm: 64, md: 96, lg: 128 };
    if (typeof this.args.size === 'number') return this.args.size;
    return sizeMap[this.args.size ?? 'md'];
  }

  get strokeWidth() {
    return this.args.strokeWidth ?? 8;
  }

  get center() {
    return this.sizeValue / 2;
  }

  get radius() {
    return (this.sizeValue - this.strokeWidth) / 2;
  }

  get circumference() {
    return 2 * Math.PI * this.radius;
  }

  get strokeDashoffset() {
    const progress = Math.max(0, Math.min(100, this.args.progress));
    return this.circumference - (progress / 100) * this.circumference;
  }

  get progressStyle() {
    return htmlSafe(
      `stroke-dasharray: ${this.circumference}; stroke-dashoffset: ${this.strokeDashoffset}`
    );
  }

  get containerStyle() {
    return htmlSafe(`width: ${this.sizeValue}px; height: ${this.sizeValue}px`);
  }

  get progressColor() {
    return this.args.progressColor ?? '#6366f1';
  }

  get trackColor() {
    return this.args.trackColor ?? '#e5e7eb';
  }

  get showLabel() {
    return this.args.showLabel !== false;
  }

  get displayLabel() {
    return this.args.label ?? `${Math.round(this.args.progress)}%`;
  }

  get labelSize() {
    if (this.sizeValue <= 64) return 'text-sm';
    if (this.sizeValue <= 96) return 'text-lg';
    return 'text-xl';
  }

  <template>
    <div class="relative flex-shrink-0" style={{this.containerStyle}} ...attributes>
      <svg
        class="w-full h-full transform -rotate-90"
        viewBox="0 0 {{this.sizeValue}} {{this.sizeValue}}"
      >
        {{! Background track }}
        <circle
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          fill="none"
          stroke={{this.trackColor}}
          stroke-width={{this.strokeWidth}}
        />
        {{! Progress arc }}
        <circle
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          fill="none"
          stroke={{this.progressColor}}
          stroke-width={{this.strokeWidth}}
          stroke-linecap="round"
          style={{this.progressStyle}}
        />
      </svg>
      {{#if this.showLabel}}
        <div class="absolute inset-0 flex flex-col items-center justify-center">
          <span
            class="font-bold text-gray-900 dark:text-gray-100 {{this.labelSize}}"
          >{{this.displayLabel}}</span>
          {{#if @sublabel}}
            <span class="text-xs text-gray-500 dark:text-gray-400">{{@sublabel}}</span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
